import React from 'react';
import getConfig from 'next/config';
import {
  HouseIcon,
  HomePriceGuideIcon,
  AuctionResultsIcon,
} from '@domain-group/fe-co-icon';
import { urlUtil } from '@domain-group/fe-helper';
import {
  FAA_LANDING_PAGE_MODULE,
  entryPoint,
  trackAppraisalFunnelEntry,
} from '../../../../tracking/landing-page';
import type { FaaLandingPageModule } from '../../../../tracking/landing-page';
import CtaItem from './components/cta-item';
import * as styles from './call-to-actions.style';

interface CallToActionsProps {
  onModuleRendered?: (module: FaaLandingPageModule) => void;
}

interface CtaConfig {
  module: FaaLandingPageModule;
  icon: React.ReactNode;
  href: string;
  label: [string, string];
  testId?: string;
  onAdditionalClick?: () => void;
}

const CallToActions = ({
  onModuleRendered,
}: CallToActionsProps): JSX.Element => {
  const { publicRuntimeConfig } = getConfig();
  const baseUrlWithProtocol = publicRuntimeConfig?.BASE_URL || '';

  const ctaConfigs: CtaConfig[] = [
    {
      module: FAA_LANDING_PAGE_MODULE.CTA_DRIVER_GET_APPRAISAL,
      icon: <HouseIcon size="custom" />,
      href: urlUtil.createUrlWithParams({
        baseUrl: baseUrlWithProtocol,
        path: '/services/appraisal',
        params: {
          exitUrl: `${baseUrlWithProtocol}/real-estate-agents`,
          'entry-point': entryPoint,
        },
      }),
      label: ['Looking to sell?', 'Get a free appraisal'],
      testId: 'appraisal-link',
      onAdditionalClick: trackAppraisalFunnelEntry,
    },
    {
      module: FAA_LANDING_PAGE_MODULE.CTA_DRIVER_TRACK_HOME,
      icon: <HomePriceGuideIcon size="custom" />,
      href: `${baseUrlWithProtocol}/owners`,
      label: ['Follow your property', 'to understand its value'],
    },
    {
      module: FAA_LANDING_PAGE_MODULE.CTA_DRIVER_RECENTLY_SOLD,
      icon: <AuctionResultsIcon size="custom" />,
      href: `${baseUrlWithProtocol}/?mode=sold`,
      label: ['See recently sold', 'properties in your area'],
    },
  ];

  return (
    <ul css={styles.callToActions}>
      {ctaConfigs.map((config) => (
        <li key={config.module} css={styles.ctaItem}>
          <CtaItem
            module={config.module}
            icon={config.icon}
            href={config.href}
            styles={styles}
            testId={config.testId}
            onModuleRendered={onModuleRendered}
            onAdditionalClick={config.onAdditionalClick}
          >
            <div>
              <span css={styles.text}>{config.label[0]}</span>
              <span css={styles.mobileOnly}>&nbsp;</span>
              <span css={styles.text}>{config.label[1]}</span>
            </div>
          </CtaItem>
        </li>
      ))}
    </ul>
  );
};

export default CallToActions;
